/*
    Created by Phat Pham
    phat@launchdeck.org
*/

import React, { useImperativeHandle, useState } from 'react';
import {
    Text,
    View,
    FlatList,
    TouchableOpacity,
    Image
} from 'react-native-web';
import PropTypes from 'prop-types';
import { getCalendarList } from '../../modules';
import { showAlert, showAppError } from '../../utils/util_helpers';
import { CustomModal, NavBar } from '../../components';
import GLOBAL from '../../configs/config_global';
import LoadingView from '../../components/component_loading_view';

export default function CalendarPickerModal(props, ref) {
    const { onSelected } = props;

    // region Local State
    const [isLoading, setLoading] = useState(false);
    const [visible, setVisible] = useState(false);
    const [calendarList, setCalendarList] = useState([]);
    // endregion

    // region Show/Hide Modal
    const showModal = () => {
        setLoading(true);
        setVisible(true);
        goFetchCalendarList();
    }

    const hideModal = () => setVisible(false);
    // endregion

    // region Function - Get Calendar ID List
    const goFetchCalendarList = () => {
        setLoading(true);
        getCalendarList()
            .then((newList) => {
                if (newList.length > 1) {
                    setCalendarList(newList);
                }
                else {
                    onSelected(newList[0].id, newList[0].title);
                    setVisible(false);
                }
            })
            .catch((error) => {
                if (error.toString().includes('"code": 401') || error.toString()?.toLowerCase().includes('sign out and sign back in to your google account')) {
                    showAlert({ text: 'Your Google login session has expired, please sign out and sign back into your Google account' });
                }
                else showAppError(error);
                setVisible(false);
            })
            .finally(() => setLoading(false))
    }
    // endregion

    // region Expose Methods
    useImperativeHandle(ref, () => ({ showModal, hideModal }), [onSelected]);
    // endregion

    // region Render Calendar Item
    const renderCalendarItem = (item) => (
        <TouchableOpacity
            onPress={() => {
                onSelected(item.id, item.title);
                hideModal();
            }}
            style={{
                flexDirection: 'row', alignItems: 'center', alignSelf: 'stretch',
                borderRadius: 8, borderWidth: 3, borderColor: GLOBAL.COLOR.TEXT_LIGHT_GRAY,
                paddingHorizontal: 24, paddingVertical: 16
            }}>
            {/* Icon */}
            <Image
                source={require('../../images/iconLineDate.png')}
                style={{ width: 28, height: 28, marginRight: 24 }}
                tintColor={GLOBAL.COLOR.BASIC.BLACK_TWO}
            />

            <View style={{ flex: 1 }}>
                {/* Calendar Name */}
                <Text style={{ color: GLOBAL.COLOR.BASIC.BLACK_TWO, fontSize: 18, fontWeight: 'bold' }} numberOfLines={1}>
                    {item.title}
                </Text>

                {/* Calendar Email */}
                <Text style={{ color: GLOBAL.COLOR.TEXT_GRAY, fontSize: 16, marginTop: 8 }} numberOfLines={1}>
                    {item.id}
                </Text>
            </View>
        </TouchableOpacity>
    )
    // endregion

    // region MAIN RENDER
    return(
        <CustomModal visible={visible}>
            {/* Nav Bar */}
            <NavBar
                leftButtonOnPress={isLoading ? () => showAlert({ text: `Please wait while we're fetching calendar data` }) : () => hideModal()}
                title={isLoading ? 'Loading Calendar Data' : 'Select Calendar'}
            />

            {/* Main Content */}
            {
                isLoading ?
                    <LoadingView isLoading={true} />
                    :
                    <View style={{ flex: 1, width: '100%' }}>
                        {/* Description */}
                        <Text style={{ color: GLOBAL.COLOR.BASIC.NEUTRAL_GREY, fontSize: 20, lineHeight: 28, textAlign: 'center', margin: 32 }}>
                            We detected more than one calendar in your Google account, please select the calendar that you want to import events from
                        </Text>

                        {/* Calendar List */}
                        <FlatList
                            style={{ flex: 1, width: '100%', backgroundColor: 'white' }}
                            contentContainerStyle={{ paddingBottom: 120, paddingHorizontal: '25%' }}
                            data={calendarList}
                            keyExtractor={(item) => item.id}
                            renderItem={({ item }) => renderCalendarItem(item)}
                            ItemSeparatorComponent={() => <View style={{ height: 16 }} />}
                            showsVerticalScrollIndicator={false}
                        />
                    </View>
            }
        </CustomModal>
    )
    // endregion
}

// Forward Ref
CalendarPickerModal = React.forwardRef(CalendarPickerModal);

// region Props
CalendarPickerModal.propTypes = {
    onSelected: PropTypes.func
}

CalendarPickerModal.defaultProps = {
    onSelected: () => {}
}
// endregion2
